import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { slugify } from "@/lib/slug";
import { resolveCurrentUserId } from "@/lib/articulos.server";



const Id = z.object({ id: z.string().uuid() });

const AutorTipo = z.enum(["bizaclia", "usuario", "agencia"]);

const ArticuloInput = z.object({
  titulo: z.string().trim().min(1).max(200),
  slug: z.string().trim().min(1).max(200).optional(),
  contenido: z.string().max(200_000).default(""),
  cover_url: z.string().url().max(1000).nullable().optional(),
  cover_credit: z.string().max(500).nullable().optional(),
  seo_title: z.string().max(200).nullable().optional(),
  seo_description: z.string().max(300).nullable().optional(),
  autor_tipo: AutorTipo.default("bizaclia"),
  autor_user_id: z.string().uuid().nullable().optional(),
  agencia_id: z.string().uuid().nullable().optional(),
  publicado: z.boolean().default(false),
});

function buildSlug(input: { slug?: string; titulo: string }) {
  const s = (input.slug && input.slug.trim()) || input.titulo;
  return slugify(s);
}

async function requireStaff(): Promise<string> {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const { data: roles } = await sb.from("user_roles").select("role").eq("user_id", userId);
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  if (!rs.includes("admin") && !rs.includes("customer_service") && !rs.includes("agente")) {
    throw new Error("Forbidden");
  }
  return userId;
}


/**
 * Aplica reglas de autoría según el rol del usuario en sesión:
 *  - admin / customer_service → puede publicar como `bizaclia`, `usuario` o `agencia`.
 *  - usuario normal → forzado a `usuario` (autor_user_id = sí mismo), salvo que elija
 *    `agencia` y pertenezca a ella.
 *  - sin sesión → se respeta el input (panel admin abierto temporalmente).
 */
async function applyAuthorRules(input: z.infer<typeof ArticuloInput>) {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const { data: roles } = await sb.from("user_roles").select("role").eq("user_id", userId);
  const rs = (roles ?? []).map((r: any) => r.role);
  const isAdmin = rs.includes("admin") || rs.includes("customer_service");

  if (isAdmin) {
    return {
      autor_tipo: input.autor_tipo,
      autor_user_id: input.autor_tipo === "usuario" ? input.autor_user_id ?? userId : null,
      agencia_id: input.autor_tipo === "agencia" ? input.agencia_id ?? null : null,
    };
  }

  // No admin: si pide agencia, validar pertenencia.
  if (input.autor_tipo === "agencia" && input.agencia_id) {
    const { data: contacto } = await sb
      .from("contactos")
      .select("agencia_id")
      .eq("user_id", userId)
      .maybeSingle();
    if (contacto?.agencia_id === input.agencia_id) {
      return { autor_tipo: "agencia" as const, autor_user_id: userId, agencia_id: input.agencia_id };
    }
  }
  // Por defecto: autor = usuario actual.
  return { autor_tipo: "usuario" as const, autor_user_id: userId, agencia_id: null };
}


// ───────── List (admin) ─────────
export const listArticulos = createServerFn({ method: "GET" }).handler(async () => {
  const sb = getSupabaseAdmin();
  const { data, error } = await sb
    .from("articulos")
    .select(
      "id, slug, titulo, cover_url, autor_tipo, autor_user_id, agencia_id, publicado, published_at, likes, dislikes, created_at, updated_at",
    )
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
});

// ───────── Listado público ordenado por score ─────────
export const listArticulosNews = createServerFn({ method: "GET" }).handler(async () => {
  const sb = getSupabaseAdmin();
  const { data, error } = await sb
    .from("articulos")
    .select(
      "id, slug, titulo, cover_url, autor_tipo, agencia_id, published_at, likes, dislikes",
    )
    .eq("publicado", true)
    .order("published_at", { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);
  const PUNTOS = 21600;
  return (data ?? [])
    .map((a) => ({
      ...a,
      score:
        (a.published_at ? Math.floor(new Date(a.published_at).getTime() / 1000) : 0) +
        ((a.likes ?? 0) - (a.dislikes ?? 0)) * PUNTOS,
    }))
    .sort((a, b) => b.score - a.score);
});

// ───────── Get por slug (público) ─────────
export const getArticuloBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { data: row, error } = await sb
      .from("articulos")
      .select("*")
      .eq("slug", data.slug)
      .eq("publicado", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

// ───────── Get by id (admin) ─────────
export const getArticulo = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { data: row, error } = await sb
      .from("articulos")
      .select("*")
      .eq("id", data.id)
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

// ───────── Create ─────────
export const createArticulo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => ArticuloInput.parse(d))
  .handler(async ({ data }) => {
    await requireStaff();
    const sb = getSupabaseAdmin();
    const slug = buildSlug(data);
    const authored = await applyAuthorRules(data);
    const insert = {
      ...data,
      ...authored,
      slug,
      published_at: data.publicado ? new Date().toISOString() : null,
    };
    const { data: row, error } = await sb
      .from("articulos")
      .insert(insert)
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, ...row };
  });


// ───────── Update ─────────
export const updateArticulo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => ArticuloInput.extend(Id.shape).parse(d))
  .handler(async ({ data }) => {
    await requireStaff();
    const sb = getSupabaseAdmin();
    const { id, ...rest } = data;
    const slug = buildSlug(rest);

    // Recoge estado actual para saber si pasa de borrador a publicado
    const { data: current } = await sb
      .from("articulos")
      .select("publicado, published_at")
      .eq("id", id)
      .single();

    const published_at =
      rest.publicado && !current?.published_at
        ? new Date().toISOString()
        : !rest.publicado
          ? null
          : current?.published_at;

    const authored = await applyAuthorRules(rest);
    const { error } = await sb
      .from("articulos")
      .update({ ...rest, ...authored, slug, published_at })
      .eq("id", id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Delete ─────────
export const deleteArticulo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await requireStaff();
    const sb = getSupabaseAdmin();
    const { error } = await sb.from("articulos").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Votar (necesita user_id; conectar a auth cuando exista) ─────────
export const voteArticulo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({
      articulo_id: z.string().uuid(),
      valor: z.union([z.literal(1), z.literal(-1), z.literal(0)]),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Unauthorized");
    const sb = getSupabaseAdmin();
    if (data.valor === 0) {
      const { error } = await sb
        .from("articulo_votos")
        .delete()
        .eq("articulo_id", data.articulo_id)
        .eq("user_id", userId);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await sb
        .from("articulo_votos")
        .upsert(
          {
            articulo_id: data.articulo_id,
            user_id: userId,
            valor: data.valor,
          },
          { onConflict: "articulo_id,user_id" },
        );
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// ───────── Unsplash search ─────────
export const searchUnsplash = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({
      q: z.string().trim().min(1).max(100),
      page: z.number().int().min(1).max(20).default(1),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    await requireStaff();
    const key = process.env.UNSPLASH_ACCESS_KEY;
    if (!key) throw new Error("UNSPLASH_ACCESS_KEY no configurada");
    const url =
      `https://api.unsplash.com/search/photos` +
      `?query=${encodeURIComponent(data.q)}` +
      `&per_page=12&page=${data.page}&orientation=landscape&content_filter=high`;
    const res = await fetch(url, {
      headers: { Authorization: `Client-ID ${key}`, "Accept-Version": "v1" },
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Unsplash ${res.status}: ${t.slice(0, 200)}`);
    }
    const j = (await res.json()) as { results?: any[] };
    return (j.results ?? []).map((p) => ({
      id: p.id as string,
      url: p.urls.regular as string,
      thumb: p.urls.small as string,
      author: p.user.name as string,
      author_url: p.user.links.html as string,
      width: p.width as number,
      height: p.height as number,
    }));
  });

export const ARTICULO_AUTOR_TIPOS = AutorTipo.options;

// ───────── Agencias ─────────
export const listAgencias = createServerFn({ method: "GET" }).handler(async () => {
  const sb = getSupabaseAdmin();
  const { data, error } = await sb
    .from("agencias")
    .select("id, slug, nombre, logo_url")
    .order("nombre", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
});

/**
 * Contexto de autoría para el formulario de artículo.
 * - Si el usuario tiene rol `admin` (o `customer_service`) → puede publicar como Bizaclia
 *   y elegir cualquier agencia.
 * - Si no, publica como sí mismo y puede elegir como autor cualquier agencia a la
 *   que pertenezca (vía contactos.agencia_id).
 * - Sin sesión (auth aún no enchufada) → tratamos como admin para no bloquear el panel.
 */
export const getArticuloAuthorContext = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();

  const [{ data: roles }, { data: contacto }] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("contactos").select("agencia_id").eq("user_id", userId).maybeSingle(),
  ]);
  const rs = (roles ?? []).map((r: any) => r.role);
  const isAdmin = rs.includes("admin") || rs.includes("customer_service");

  let agencias: { id: string; nombre: string }[] = [];
  if (isAdmin) {
    const { data } = await sb.from("agencias").select("id, nombre").order("nombre");
    agencias = data ?? [];
  } else if (contacto?.agencia_id) {
    const { data } = await sb
      .from("agencias")
      .select("id, nombre")
      .eq("id", contacto.agencia_id);
    agencias = data ?? [];
  }

  return { userId, isAdmin, agencias };
});

